import { formatSnapshotDate } from '../../utils/snoop/format'

function filterLabel(filters = {}) {
  if (!filters || !Object.keys(filters).length) return null

  const andSources = Array.isArray(filters.all_follows_and)
    ? filters.all_follows_and
    : null
  const andNote =
    andSources?.length >= 2 ? `AND ${andSources.length} snoop sources` : ''

  // A server-side search replaces the New Follows floor entirely — say so, since the
  // row set is then the whole snapshot rather than the default browse cohort.
  if (filters.q) {
    return andNote ? `Search “${filters.q}” · ${andNote}` : `Search “${filters.q}”`
  }

  // Only show a New Follows floor when it actually gates the query (≥ 1).
  // min_new_follows=0 (source filter / search) is "no floor" — hide the chip.
  const minNew = filters.min_new_follows
  if (minNew != null && Number(minNew) >= 1) {
    return andNote
      ? `New Follows ≥ ${minNew} · ${andNote}`
      : `New Follows ≥ ${minNew}`
  }
  if (andNote) return andNote

  if (filters.min_daily_growth != null) {
    return `Daily growth ≥ ${filters.min_daily_growth}`
  }
  if (
    Array.isArray(filters.excluded_statuses) &&
    filters.excluded_statuses.length > 0
  ) {
    return `excl. ${filters.excluded_statuses.join('/')}`
  }
  const included = []
  if (filters.include_ignored === true) included.push('Destroy/Junk')
  if (filters.include_cm === true) included.push('CM')
  if (included.length) return `incl. ${included.join(' · ')}`
  return null
}

/** Human-readable scope for the badge (DB scope values are opaque). */
function scopeDescription(table, scope) {
  if (!scope) return null
  const s = String(scope).toLowerCase()
  if (s === 'mapping') return 'mapping table'
  // snoop_summary keeps only blank snoop-mapping Status (non-blank incl. CORE excluded)
  if (table === 'snoop_summary') {
    return 'blank Status'
  }
  if (s === 'unsigned') return 'Priority / Archive / Chartmetric'
  if (s === 'roster') return 'Roster'
  if (s === 'distro') return 'Distro'
  if (s === 'all') return 'all'
  return scope
}

export default function SnapshotBadge({
  snapshotDate,
  rowCount,
  totalCount = null,
  table,
  scope,
  filters,
  parity,
  source = null,
}) {
  const threshold = filterLabel(filters)
  const scopeText = scopeDescription(table, scope)
  const rowsLabel =
    totalCount != null && totalCount > (rowCount ?? 0)
      ? `${rowCount ?? 0} of ${totalCount}`
      : String(rowCount ?? 0)
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
      {snapshotDate ? (
        <span className="rounded-full border border-gray-200 bg-white px-3 py-1">
          Snapshot: <span className="font-medium text-gray-800">{formatSnapshotDate(snapshotDate)}</span>
        </span>
      ) : null}
      <span className="rounded-full border border-gray-200 bg-white px-3 py-1">
        Rows: <span className="font-medium text-gray-800">{rowsLabel}</span>
      </span>
      {source === 'live_fallback' ? (
        <span
          className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-900"
          title="luminate_track_summary was empty — metrics computed live (slower)"
        >
          Live compute fallback
        </span>
      ) : null}
      {parity && parity.aligned != null ? (
        <span
          className={
            parity.aligned
              ? 'rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-800'
              : 'rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-900'
          }
        >
          {parity.aligned ? 'In sync' : 'Drift detected'}
        </span>
      ) : parity && (parity.aligned === null || parity.skipped || parity.blocked) ? (
        <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-gray-600">
          Postgres
        </span>
      ) : null}
      {threshold ? (
        <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-blue-700">
          Filter: <span className="font-medium">{threshold}</span>
        </span>
      ) : null}
      {scopeText ? (
        <span className="rounded-full border border-gray-200 bg-white px-3 py-1">
          Scope: <span className="font-medium text-gray-800">{scopeText}</span>
        </span>
      ) : null}
    </div>
  )
}
